import React from 'react'
import { createRoot } from 'react-dom/client'
import { Router } from 'react-router-dom'
import 'date-fns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import 'intersection-observer'
import { history, AuthContextProvider, authRoutes } from 'packages/core'
import { Loader, NotificationsProvider } from 'packages/eid-ui'
import { Switch, Route } from 'react-router-dom'
import * as serviceWorker from './serviceWorker'
import AppRoutes from 'containers/AppRoutes'
import './i18n'
import 'packages/eid-ui/globalStyles.css'
import config from 'config'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import 'moment/locale/de'
import 'moment/locale/fr'
import 'moment/locale/es'
import './index.css'
import { AppContextProvider } from 'appContext'
import CookieChecker from 'packages/core/auth/cookieChecker'

const queryClientOptions = {
    defaultOptions: {
        queries: {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
}

const queryClient = new QueryClient(queryClientOptions)

const MOUNT_NODE = document.getElementById('root')

const MainApp = (
    <React.StrictMode>
        <Router history={history}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <QueryClientProvider client={queryClient}>
                    <CookieChecker config={config}>
                        <AuthContextProvider
                            config={{
                                settings: {
                                    authority: config.AUTHORITY_URL,
                                    client_id: config.CLIENT_ID,
                                    scope: config.SCOPE,
                                    response_type:
                                        config.RESPONSE_TYPE ?? 'code',
                                },
                                initialValue: {
                                    displayName: '',
                                    currentUserId: '',
                                },
                                extractAuthData: async (token) => {
                                    const rawResponse = await fetch(
                                        `${config.API_URL}/api/v1/me/user`,
                                        {
                                            headers: {
                                                Authorization: `Bearer ${token}`,
                                            },
                                        },
                                    )
                                    const response = await rawResponse.json()
                                    return {
                                        displayName: response.displayName,
                                        currentUserId: response.id,
                                    }
                                },
                            }}
                        >
                            <AppContextProvider>
                                <NotificationsProvider>
                                    <Switch>
                                        {authRoutes.map((ar) => (
                                            <Route
                                                key={ar.path}
                                                exact
                                                path={ar.path}
                                                render={(props) => (
                                                    <ar.component
                                                        {...props}
                                                        render={() => (
                                                            <Loader />
                                                        )}
                                                    />
                                                )}
                                            />
                                        ))}
                                        <AppRoutes />
                                    </Switch>
                                </NotificationsProvider>
                            </AppContextProvider>
                        </AuthContextProvider>
                    </CookieChecker>
                    {process.env.NODE_ENV === 'development' && (
                        <ReactQueryDevtools />
                    )}
                </QueryClientProvider>
            </LocalizationProvider>
        </Router>
    </React.StrictMode>
)

const root = createRoot(MOUNT_NODE)

root.render(MainApp)

serviceWorker.unregister()
